/**
 * /policies/payroll/bank-templates — Session 3c PR 19.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { BankTemplateItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollBankTemplatesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-bank-templates", le],
    queryFn: () => session3cApi.listBankTemplates(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<BankTemplateItem>[] = [
    {
      headerKey: "policies.payroll_bank_templates.col.bank",
      label: "Bank",
      render: (r) => <span className="font-medium">{r.bank_name_en}</span>,
    },
    {
      headerKey: "policies.payroll_bank_templates.col.sif",
      label: "SIF template",
      render: (r) => <span className="font-mono text-xs">{r.sif_template_version}</span>,
    },
    {
      headerKey: "policies.payroll_bank_templates.col.primary",
      label: "Primary",
      render: (r) => <span className={`text-xs ${r.is_primary ? "font-semibold text-brand-deep" : "text-text-tertiary"}`}>{r.is_primary ? "yes" : "—"}</span>,
    },
    {
      headerKey: "policies.payroll_bank_templates.col.last_used",
      label: "Last used",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.last_used_at ?? "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<BankTemplateItem>
      title={t("policies.payroll_bank_templates.title", "Bank Format Templates")}
      subtitle={t("policies.payroll_bank_templates.subtitle", "Per-LE bank file template selection — SIF (5 KSA banks) + custom formats.")}
      agent="rawatib"
      domain="payroll_bank_templates"
      newButtonLabel={t("policies.payroll_bank_templates.cta.new", "Add Bank")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_bank_templates.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_bank_templates.suggestion.clean_body", "Each bank's SIF template version drives /operations/payroll/bank-file generation per period."),
          primaryActionLabel: t("policies.payroll_bank_templates.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-bank-templates-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_bank_templates.title", "Bank Format Templates"),
        window_label: t("policies.payroll_bank_templates.window", "5 KSA banks"),
      }}
    />
  );
}
